<template>
  <div class="glossary-view">
    <!-- Header Banner -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card bg-gradient-dark border-0 shadow-lg text-white" style="background: linear-gradient(135deg, #121829 0%, #0a0d17 100%); border-left: 4px solid #3b82f6 !important; border-radius: 14px;">
          <div class="card-body p-3 p-md-4">
            <div class="row align-items-center">
              <div class="col-lg-8">
                <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 8px;">
                  <span class="badge badge-primary font-weight-bold px-3 py-1">
                    <i class="mdi mdi-book-open-page-variant mr-1"></i>
                    {{ isEn ? 'Knowledge Hub & Glossary' : 'Knowledge Hub & Glosarium' }}
                  </span>
                  <span class="badge badge-outline-warning">
                    <i class="mdi mdi-lightning-bolt mr-1"></i>
                    {{ isEn ? 'Complete Gamification Guide' : 'Panduan Lengkap Gamifikasi' }}
                  </span>
                  <span class="badge badge-outline-success">
                    <i class="mdi mdi-shield-check mr-1"></i>
                    {{ isEn ? 'Gamiteam 2.0 Official Guide' : 'Panduan Resmi Gamiteam 2.0' }}
                  </span>
                </div>
                <h2 class="font-weight-bold mb-2 text-white">
                  {{ isEn ? 'Terminology Glossary & System Guide' : 'Glosarium Istilah & Panduan Sistem' }}
                </h2>
                <p class="text-muted mb-0" style="font-size: 14px; line-height: 1.6;">
                  {{ isEn
                    ? 'Comprehensive guide for all concepts, terminology, game mechanics, mathematical formulas, and role workflows (Super Admin, Game Owner, Game Maker, and Game Player) across the Gamiteam platform.'
                    : 'Panduan komprehensif seluruh konsep, terminologi, mekanik game, formula matematis, serta alur kerja role (Super Admin, Game Owner, Game Maker, dan Game Player) di platform Gamiteam.' }}
                </p>
              </div>
              <div class="col-lg-4 text-lg-right mt-3 mt-lg-0">
                <div class="d-flex flex-column flex-sm-row justify-content-lg-end" style="gap: 8px;">
                  <button class="btn btn-outline-info font-weight-bold" @click="activeMainTab = 'calculator'">
                    <i class="mdi mdi-calculator mr-1"></i> {{ isEn ? 'Formula Simulator' : 'Simulator Formula' }}
                  </button>
                  <button class="btn btn-primary font-weight-bold" @click="printOrExport">
                    <i class="mdi mdi-printer mr-1"></i> {{ isEn ? 'Print Guide' : 'Cetak Panduan' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Tabs: Glosarium, Alur Peran, Formula & Kalkulator -->
    <div class="row mb-4">
      <div class="col-12">
        <ul class="nav nav-pills custom-pills p-2 bg-dark rounded-lg border border-secondary" style="gap: 8px;">
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'glossary' }"
              @click.prevent="activeMainTab = 'glossary'"
            >
              <i class="mdi mdi-format-list-bulleted-type mr-1"></i>
              {{ isEn ? 'Terminology Glossary' : 'Kamus Istilah' }} ({{ filteredTerms.length }}/{{ terms.length }})
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'workflows' }"
              @click.prevent="activeMainTab = 'workflows'"
            >
              <i class="mdi mdi-transit-connection-variant mr-1"></i>
              {{ isEn ? 'Role Journeys & Workflows' : 'Panduan Alur Peran (Role Journeys)' }}
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'calculator' }"
              @click.prevent="activeMainTab = 'calculator'"
            >
              <i class="mdi mdi-function-variant mr-1"></i>
              {{ isEn ? 'Formulas & Simulation Calculator' : 'Formula & Kalkulator Simulasi' }}
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link font-weight-bold py-2 px-3 cursor-pointer"
              :class="{ active: activeMainTab === 'faq' }"
              @click.prevent="activeMainTab = 'faq'"
            >
              <i class="mdi mdi-frequently-asked-questions mr-1"></i>
              {{ isEn ? 'FAQ & Gamification Tips' : 'FAQ & Tips Gamifikasi' }}
            </a>
          </li>
        </ul>
      </div>
    </div>

    <!-- TAB 1: GLOSARIUM ISTILAH -->
    <div v-if="activeMainTab === 'glossary'">
      <!-- Search & Category Filter Controls -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card bg-dark border-secondary p-3">
            <div class="row align-items-center">
              <div class="col-md-6 mb-3 mb-md-0">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text bg-secondary border-0 text-white">
                      <i class="mdi mdi-magnify"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    v-model="searchQuery"
                    class="form-control bg-secondary text-white border-0"
                    :placeholder="isEn ? 'Search terms, formulas, or examples (e.g. Gold, Boss Raid, Burn Rate, Gemini OCR)...' : 'Cari istilah, formula, atau contoh (mis: Gold, Boss Raid, Burn Rate, Gemini OCR)...'"
                  />
                  <div class="input-group-append" v-if="searchQuery">
                    <button class="btn btn-outline-light border-0" @click="searchQuery = ''">
                      <i class="mdi mdi-close"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-6 text-md-right">
                <span class="text-muted small mr-2">{{ isEn ? 'Filter Category:' : 'Filter Kategori:' }}</span>
                <div class="d-inline-flex flex-wrap" style="gap: 4px;">
                  <button
                    v-for="cat in displayedCategories"
                    :key="cat.id"
                    class="btn btn-xs py-1 px-2 font-weight-bold rounded-pill"
                    :class="selectedCategory === cat.id ? 'btn-warning text-dark' : 'btn-outline-secondary text-light'"
                    @click="selectedCategory = cat.id"
                  >
                    {{ cat.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Metrics Summary -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'Total Documented Terms' : 'Total Istilah Terdata' }}</div>
            <h3 class="text-primary font-weight-bold mb-0 mt-1">{{ terms.length }}</h3>
            <span class="small text-muted">{{ isEn ? 'Covering all modules' : 'Mencakup seluruh modul' }}</span>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'Gamification Pillars' : 'Kategori Gamifikasi' }}</div>
            <h3 class="text-warning font-weight-bold mb-0 mt-1">{{ categories.length - 1 }} {{ isEn ? 'Pillars' : 'Pilar' }}</h3>
            <span class="small text-muted">{{ isEn ? 'Economy, Quests, Rules, Raid' : 'Ekonomi, Misi, Aturan, Raid' }}</span>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'Integrated AI Engine' : 'Mekanik AI Terintegrasi' }}</div>
            <h3 class="text-success font-weight-bold mb-0 mt-1">{{ isEn ? 'Gemini OCR' : 'OCR Gemini' }}</h3>
            <span class="small text-muted">{{ isEn ? 'Automated proof verification' : 'Verifikasi otomatis bukti kerja' }}</span>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <div class="card bg-dark border-secondary p-3 text-center">
            <div class="text-muted small">{{ isEn ? 'SOP Governance' : 'Tata Kelola SOP' }}</div>
            <h3 class="text-info font-weight-bold mb-0 mt-1">{{ isEn ? 'Audited' : 'Teraudit' }}</h3>
            <span class="small text-muted">{{ isEn ? 'Fair penalty & compensation' : 'Penalti adil & pemulihan' }}</span>
          </div>
        </div>
      </div>

      <!-- Terms Grid -->
      <div class="row" v-if="filteredTerms.length > 0">
        <div
          v-for="term in filteredTerms"
          :key="term.id"
          class="col-lg-6 mb-4 stretch-card"
        >
          <div class="card bg-dark border-secondary shadow-sm term-card h-100">
            <div class="card-body p-4 d-flex flex-column justify-content-between">
              <div>
                <!-- Top Badge Row -->
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="badge" :class="getCategoryBadgeClass(term.category)">
                    {{ getCategoryLabel(term.category) }}
                  </span>
                  <span class="badge" :class="getImpactBadgeClass(term.impact)">
                    <i :class="getImpactIcon(term.impact)" class="mr-1" aria-hidden="true"></i>
                    {{ isEn ? (term.impactLabelEn || term.impactLabel) : term.impactLabel }}
                  </span>
                </div>

                <!-- Term Title -->
                <h4 class="text-white font-weight-bold mb-1 d-flex align-items-center">
                  <i class="mdi mr-2 text-warning" :class="term.icon" style="font-size: 20px;" aria-hidden="true"></i>
                  <span>{{ isEn ? (term.nameEn || term.name) : term.name }}</span>
                </h4>

                <!-- Short Definition -->
                <p class="text-light mt-2 mb-3" style="font-size: 14px; line-height: 1.6;">
                  {{ isEn ? (term.definitionEn || term.definition) : term.definition }}
                </p>

                <!-- Detailed Mechanics & Workplace Example -->
                <div class="bg-secondary p-3 rounded mb-3 text-white-50" style="background-color: #1a2030 !important; font-size: 13px;">
                  <div class="mb-1 text-warning font-weight-bold">
                    <i class="mdi mdi-briefcase-check mr-1" aria-hidden="true"></i> {{ isEn ? 'Workplace Application:' : 'Penerapan di Tempat Kerja:' }}
                  </div>
                  <div class="text-light">{{ isEn ? (term.workplaceExampleEn || term.workplaceExample) : term.workplaceExample }}</div>
                </div>

                <!-- Formula if present -->
                <div v-if="term.formula" class="p-2 mb-3 rounded border border-info bg-dark text-info font-monospace small">
                  <strong><i class="mdi mdi-function mr-1" aria-hidden="true"></i> {{ isEn ? 'Formula:' : 'Rumus:' }}</strong> <code>{{ isEn ? (term.formulaEn || term.formula) : term.formula }}</code>
                </div>
              </div>

              <!-- Actionable Role Tip Footer -->
              <div class="pt-3 border-top border-secondary d-flex justify-content-between align-items-center">
                <span class="small text-muted">
                  <i class="mdi mdi-lightbulb-on text-warning mr-1" aria-hidden="true"></i>
                  <strong>{{ isEn ? ('Tips for ' + (term.targetRoleEn || term.targetRole) + ':') : ('Tips ' + term.targetRole + ':') }}</strong> {{ isEn ? (term.tipEn || term.tip) : term.tip }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty Search State -->
      <div class="row" v-else>
        <div class="col-12 text-center py-5">
          <div class="display-1 text-muted"><i class="mdi mdi-file-search-outline" aria-hidden="true"></i></div>
          <h4 class="text-white mt-3">{{ isEn ? 'No terms found' : 'Istilah tidak ditemukan' }}</h4>
          <p class="text-muted">{{ isEn ? ('No terms matched the keyword "' + searchQuery + '".') : ('Tidak ada istilah yang cocok dengan kata kunci "' + searchQuery + '".') }}</p>
          <button class="btn btn-outline-warning mt-2" @click="searchQuery = ''; selectedCategory = 'all'">
            {{ isEn ? 'Reset Search' : 'Atur Ulang Pencarian' }}
          </button>
        </div>
      </div>
    </div>

    <!-- TAB 2: PANDUAN ALUR PERAN (WORKFLOWS) -->
    <div v-if="activeMainTab === 'workflows'">
      <div class="row mb-4">
        <!-- Role Selector Pills -->
        <div class="col-12 text-center mb-4">
          <div class="btn-group flex-wrap" role="group">
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'owner' ? 'btn-warning text-dark' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'owner'"
            >
              <i class="mdi mdi-crown mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Game Owner (Executive & Sponsor)' : 'Panduan Pemilik Ruang Kerja (Eksekutif)' }}
            </button>
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'maker' ? 'btn-success text-dark' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'maker'"
            >
              <i class="mdi mdi-hammer-wrench mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Game Maker (HR & Team Lead)' : 'Panduan Manajer & Perancang Misi' }}
            </button>
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'player' ? 'btn-info text-dark' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'player'"
            >
              <i class="mdi mdi-account-star mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Player (Team Member)' : 'Panduan Karyawan & Anggota Tim' }}
            </button>
            <button
              class="btn px-4 py-2 font-weight-bold"
              :class="selectedRoleWorkflow === 'superadmin' ? 'btn-danger text-white' : 'btn-outline-secondary text-white'"
              @click="selectedRoleWorkflow = 'superadmin'"
            >
              <i class="mdi mdi-shield-crown mr-1" aria-hidden="true"></i>
              {{ isEn ? 'Superadmin (Platform Operator)' : 'Panduan Operator Platform' }}
            </button>
          </div>
        </div>

        <!-- Workflow Details Content -->
        <div class="col-12">
          <div class="card bg-dark p-4" :class="currentWorkflowData.cardClass">
            <div class="d-flex align-items-center mb-3">
              <i class="mdi display-4 mr-3" :class="[currentWorkflowData.icon, currentWorkflowData.titleClass]" aria-hidden="true"></i>
              <div>
                <h3 class="font-weight-bold mb-1" :class="currentWorkflowData.titleClass">
                  {{ isEn ? currentWorkflowData.titleEn : currentWorkflowData.titleId }}
                </h3>
                <p class="text-muted mb-0">
                  {{ isEn ? currentWorkflowData.subtitleEn : currentWorkflowData.subtitleId }}
                </p>
              </div>
            </div>

            <div class="timeline-steps mt-4">
              <div
                v-for="(step, sIdx) in currentWorkflowData.steps"
                :key="sIdx"
                class="step-card mb-3 p-3 bg-secondary rounded"
                :class="currentWorkflowData.stepClass"
              >
                <h5 class="text-white font-weight-bold">
                  <span class="badge mr-2" :class="currentWorkflowData.badgeClass">
                    {{ isEn ? ('Step ' + (sIdx + 1)) : ('Langkah ' + (sIdx + 1)) }}
                  </span>
                  {{ isEn ? step.titleEn : step.titleId }}
                </h5>
                <p class="text-muted mb-0 small">
                  {{ isEn ? step.descEn : step.descId }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 3: FORMULA & SIMULATOR KALKULATOR -->
    <div v-if="activeMainTab === 'calculator'">
      <div class="row">
        <!-- Interactive Simulator Widget -->
        <div class="col-lg-5 mb-4">
          <div class="card bg-dark border-primary p-4 h-100">
            <div class="d-flex align-items-center mb-3">
              <i class="mdi mdi-calculator-variant text-primary display-4 mr-3"></i>
              <div>
                <h4 class="text-white font-weight-bold mb-1">
                  {{ isEn ? 'Gamification Calculation Simulator' : 'Simulator Kalkulasi Gamifikasi' }}
                </h4>
                <p class="text-muted small mb-0">
                  {{ isEn ? 'Enter simulation values to view live engine formulas.' : 'Masukkan angka simulasi untuk melihat output formula secara live.' }}
                </p>
              </div>
            </div>

            <hr class="border-secondary my-3" />

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Total Player EXP:' : 'Total EXP Pemain:' }}
              </label>
              <input type="number" v-model.number="calcExp" class="form-control bg-secondary text-white border-0" min="0" />
            </div>

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Consecutive Daily Streak (Days):' : 'Streak Berturut-turut (Hari):' }}
              </label>
              <input type="number" v-model.number="calcStreak" class="form-control bg-secondary text-white border-0" min="1" max="100" />
            </div>

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Gold Earned This Month:' : 'Gold Diperoleh Bulan Ini:' }}
              </label>
              <input type="number" v-model.number="calcGoldEarned" class="form-control bg-secondary text-white border-0" min="0" />
            </div>

            <div class="form-group mb-3">
              <label class="text-white small font-weight-bold">
                {{ isEn ? 'Gold Redeemed for Rewards:' : 'Gold Ditukarkan ke Hadiah:' }}
              </label>
              <input type="number" v-model.number="calcGoldRedeemed" class="form-control bg-secondary text-white border-0" min="0" />
            </div>

            <div class="bg-secondary p-3 rounded mt-3" style="background-color: #121828 !important;">
              <h6 class="text-warning font-weight-bold mb-2">
                {{ isEn ? 'System Calculation Results:' : 'Hasil Perhitungan Sistem:' }}
              </h6>
              <div class="d-flex justify-content-between mb-1 small text-light">
                <span>{{ isEn ? 'Character Level:' : 'Level Karakter:' }}</span>
                <strong class="text-white">Level {{ calculatedLevel }} ({{ currentTierName }})</strong>
              </div>
              <div class="d-flex justify-content-between mb-1 small text-light">
                <span>{{ isEn ? 'Streak Multiplier:' : 'Streak Multiplier:' }}</span>
                <strong class="text-info">{{ calculatedStreakMultiplier }}x Bonus</strong>
              </div>
              <div class="d-flex justify-content-between mb-1 small text-light">
                <span>{{ isEn ? 'Damage to Boss Raid:' : 'Damage ke Boss Raid:' }}</span>
                <strong class="text-danger font-weight-bold">{{ calculatedBossDamage.toLocaleString() }} DMG</strong>
              </div>
              <div class="d-flex justify-content-between small text-light">
                <span>{{ isEn ? 'Gold Burn Rate:' : 'Gold Burn Rate:' }}</span>
                <strong :class="burnRateColor">{{ calculatedBurnRate }}% ({{ burnRateStatus }})</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Formula Reference Cards -->
        <div class="col-lg-7 mb-4">
          <div class="card bg-dark border-secondary p-4 h-100">
            <h4 class="text-white font-weight-bold mb-3 d-flex align-items-center">
              <i class="mdi mdi-book-open-outline text-warning mr-2"></i>
              {{ isEn ? 'System Mathematical Formula Reference' : 'Lembar Referensi Formula Matematis Sistem' }}
            </h4>

            <div class="formula-item mb-3 p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-warning">{{ isEn ? '1. Level Up Threshold Formula' : '1. Formula Level Up Threshold' }}</strong>
                <span class="badge badge-outline-warning">{{ isEn ? 'Leveling' : 'Kenaikan Level' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Determines the EXP threshold required to advance to the next level.' : 'Menentukan ambang batas EXP yang dibutuhkan untuk naik ke level berikutnya.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>EXP_Level(N) = N × 100</code>
              </div>
            </div>

            <div class="formula-item mb-3 p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-danger">{{ isEn ? '2. Quarterly Boss Raid Damage Formula' : '2. Formula Damage Boss Raid Kuartalan' }}</strong>
                <span class="badge badge-outline-danger">{{ isEn ? 'World Realm' : 'Arena Virtual' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Each employee work contribution point is converted into direct attack damage against the team Boss Monster.' : 'Setiap poin kontribusi kerja pemain dikonversi menjadi serangan langsung ke Monster Boss tim.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>Boss_Damage = (EXP + (Gold × 2)) × (1 + (Streak × 0.05))</code>
              </div>
            </div>

            <div class="formula-item mb-3 p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-success">{{ isEn ? '3. Gold Burn Rate Formula (Company Economy)' : '3. Formula Gold Burn Rate (Ekonomi Perusahaan)' }}</strong>
                <span class="badge badge-outline-success">{{ isEn ? 'Executive ROI' : 'ROI Eksekutif' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Evaluates corporate gold currency liquidity to maintain healthy and balanced reward expenditures.' : 'Mengevaluasi likuiditas koin emas perusahaan agar pengeluaran hadiah tetap seimbang dan sehat.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>{{ isEn ? 'Burn_Rate = (Gold_Redeemed / Total_Gold_Minted) × 100%' : 'Burn_Rate = (Gold_Ditukarkan / Total_Gold_Diterbitkan) × 100%' }}</code>
                <div class="text-muted small mt-1">
                  {{ isEn ? 'Healthy Zone: 40% - 75% | <30% (Hoarding Points) | >90% (Over-burning)' : 'Zona Sehat: 40% - 75% | <30% (Karyawan Menimbun Poin) | >90% (Over-burning)' }}
                </div>
              </div>
            </div>

            <div class="formula-item p-3 bg-secondary rounded">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-primary">{{ isEn ? '4. Seat Quota Utilization Formula' : '4. Formula Seat Quota Utilization' }}</strong>
                <span class="badge badge-outline-primary">{{ isEn ? 'Licensing & Quota' : 'Lisensi & Kuota' }}</span>
              </div>
              <p class="text-light small mb-1">
                {{ isEn ? 'Employee seat utilization efficiency relative to paid license capacity.' : 'Efisiensi utilisasi kursi karyawan terdaftar dibandingkan kapasitas lisensi yang dibayar.' }}
              </p>
              <div class="p-2 bg-dark rounded text-info font-monospace small">
                <code>{{ isEn ? 'Seat_Utilization = (Active_Employees / Total_Seat_Quota) × 100%' : 'Seat_Utilization = (Karyawan_Aktif / Total_Seat_Quota) × 100%' }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB 4: FAQ & BEST PRACTICES -->
    <div v-if="activeMainTab === 'faq'">
      <div class="row">
        <div class="col-lg-8 mx-auto">
          <div class="card bg-dark border-secondary p-4">
            <h4 class="text-white font-weight-bold mb-4 text-center">
              <i class="mdi mdi-frequently-asked-questions text-warning mr-2"></i>
              {{ isEn ? 'Frequently Asked Questions (FAQ) & Best Practices' : 'Pertanyaan yang Sering Diajukan (FAQ) & Best Practices' }}
            </h4>

            <div class="accordion" id="glossaryAccordion">
              <div
                v-for="(faq, idx) in faqs"
                :key="idx"
                class="card bg-secondary border-0 mb-3 rounded"
              >
                <div class="card-header p-3 cursor-pointer" @click="toggleFaq(idx)">
                  <h6 class="mb-0 text-white font-weight-bold d-flex justify-content-between align-items-center">
                    <span><i class="mdi mdi-help-circle-outline text-warning mr-2"></i> {{ isEn ? faq.qEn : faq.qId }}</span>
                    <i class="mdi" :class="expandedFaq === idx ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
                  </h6>
                </div>
                <div v-if="expandedFaq === idx" class="card-body p-3 pt-0 text-light small border-top border-secondary">
                  <p class="mb-0" style="line-height: 1.6;">{{ isEn ? faq.aEn : faq.aId }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GlossaryView",
  data() {
    return {
      activeMainTab: "glossary", // glossary | workflows | calculator | faq
      searchQuery: "",
      selectedCategory: "all",
      selectedRoleWorkflow: "owner",
      expandedFaq: 0,

      // Calculator fields
      calcExp: 350,
      calcStreak: 5,
      calcGoldEarned: 500,
      calcGoldRedeemed: 250,

      categories: [
        { id: "all", labelId: "Semua", labelEn: "All" },
        { id: "currency", labelId: "Mata Uang & Poin", labelEn: "Currency & Points" },
        { id: "quest", labelId: "Quests & Misi", labelEn: "Quests & KPI" },
        { id: "discipline", labelId: "Tata Tertib & Sanksi", labelEn: "Discipline & Sanctions" },
        { id: "raid", labelId: "Boss Raid & Realm", labelEn: "Boss Raids & Realms" },
        { id: "org", labelId: "Tim & Apresiasi", labelEn: "Teams & Recognition" },
        { id: "role", labelId: "Peran & Hak Akses", labelEn: "Roles & Permissions" },
        { id: "billing", labelId: "Lisensi & Billing", labelEn: "Billing & Licensing" },
      ],

      roleWorkflows: {
        owner: {
          icon: "mdi-crown",
          cardClass: "border-warning",
          titleClass: "text-warning",
          badgeClass: "badge-warning text-dark",
          stepClass: "border-left-warning",
          titleId: "Alur Kerja Utama Game Owner",
          titleEn: "Main Workflow for Game Owner",
          subtitleId: "Fokus: Return on Investment (ROI), Manajemen Kapasitas Kursi (Seat Quota), dan Pembatasan Finansial Poin.",
          subtitleEn: "Focus: Return on Investment (ROI), Seat Quota Allocation, and Financial Risk Safeguards.",
          steps: [
            {
              titleId: "Registrasi & Setup Ruang Kerja (Company Workspace)",
              titleEn: "Registration & Company Workspace Setup",
              descId: "Game Owner mendaftarkan perusahaan, memilih paket langganan (Starter, Growth, Enterprise), dan mendapatkan alokasi kuota kursi karyawan awal.",
              descEn: "Game Owner registers the enterprise workspace, selects an onboarding plan (Starter, Growth, Enterprise), and claims initial seat quota."
            },
            {
              titleId: "Menentukan Valuasi Gold & Rasio Hadiah Finansial",
              titleEn: "Configure Gold Valuation & Financial Reward Liability",
              descId: "Menentukan rasio konversi 1 Gold = Rp 1.000 (atau sesuai budget perusahaan), dan menetapkan batas liabilitas maksimum reward bulanan agar keuangan terkendali.",
              descEn: "Establish currency conversion ratio (e.g. 1 Gold = IDR 1,000) and set monthly redemption liability ceilings to ensure budget predictability."
            },
            {
              titleId: "Mendelegasikan Game Maker untuk Divisi",
              titleEn: "Appoint Game Makers for Company Divisions",
              descId: "Menunjuk manajer/HR sebagai Game Maker untuk menyusun aturan, KPI divisi, tata tertib, serta hadiah katalog di Reward Shop.",
              descEn: "Assign managers and HR leaders as Game Makers to construct division KPIs, SOP checklists, and curated Reward Shop catalogs."
            },
            {
              titleId: "Memonitor Macro Analytics & Gold Burn Rate",
              titleEn: "Monitor Macro Analytics & Gold Burn Rate",
              descId: "Melalui menu Executive Analytics, Owner memantau rasio pembakaran gold (Burn Rate), skor kepuasan tim, top performer antar divisi, dan kesehatan ekonomi poin.",
              descEn: "Via Executive Analytics, monitor point burn rates, team velocity, division performance heatmaps, and total reward liability health."
            },
            {
              titleId: "Ekspansi Kuota Eceran (Add-on Seats)",
              titleEn: "On-demand License Expansion (Add-on Seats)",
              descId: "Saat merekrut karyawan baru, Owner dapat langsung membeli kuota kursi eceran secara instan lewat Midtrans tanpa perlu mengganti paket langganan tahunan.",
              descEn: "When onboarding new hires, purchase scalable add-on seats instantly via payment gateway without renegotiating annual contracts."
            }
          ]
        },
        maker: {
          icon: "mdi-hammer-wrench",
          cardClass: "border-success",
          titleClass: "text-success",
          badgeClass: "badge-success text-dark",
          stepClass: "border-left-success",
          titleId: "Alur Kerja Utama Game Maker",
          titleEn: "Main Workflow for Game Maker (Studio Manager)",
          subtitleId: "Fokus: Perancangan Misi KPI, Disiplin Tata Tertib SOP, AI Vision OCR, dan Verifikasi Hasil Kerja.",
          subtitleEn: "Focus: KPI Quest Blueprinting, SOP Disciplinary Framework, AI Vision OCR, and Submission Verifications.",
          steps: [
            {
              titleId: "Merancang Misi Harian & Misi Sprint (Quest Studio)",
              titleEn: "Design Daily Habits & Sprint Quests (Quest Studio)",
              descId: "Membuat misi kerja operasional berulang (daily habit) dan target project mingguan dengan bobot EXP dan Gold proporsional.",
              descEn: "Construct daily operational habit loops and weekly sprint targets with calibrated EXP and Gold coin rewards."
            },
            {
              titleId: "Menerapkan Aturan Tata Tertib (SOP Consequence)",
              titleEn: "Establish Standard Operating Procedure (SOP) Consequences",
              descId: "Menyusun aturan penalti gold transparan atas kelalaian kerja (misal: keterlambatan, kesalahan input data, atau pelanggaran keselamatan kerja).",
              descEn: "Define transparent point consequences for operational lapses (e.g. late submissions, data entry mistakes, or missed standups)."
            },
            {
              titleId: "Menyediakan Misi Kompensasi (Pemulihan Penalti & Reputasi)",
              titleEn: "Deploy Recovery Tasks (Compensation Quests)",
              descId: "Menyiapkan tugas darurat perbaikan sehingga karyawan yang terkena sanksi dapat memulihkan saldo gold dan reputasi kerjanya.",
              descEn: "Provide constructive remediation tasks allowing penalized players to restore deducted gold and maintain high team morale."
            },
            {
              titleId: "Approval Verifikasi Bukti Kerja dengan Gemini AI Vision",
              titleEn: "Submission Approvals with Gemini AI Vision Verification",
              descId: "Memeriksa lampiran Proof of Work (PoW) karyawan dengan bantuan Gemini AI OCR yang secara otomatis membaca teks foto dalam hitungan detik.",
              descEn: "Review Proof of Work deliverables accelerated by Gemini Vision OCR which automatically extracts verification telemetry."
            },
            {
              titleId: "Kurasi Katalog Hadiah (Loot Vault & Reward Shop)",
              titleEn: "Curate Reward Shop Merchandise & Loot Vault",
              descId: "Menambahkan reward fisik atau voucher digital yang dapat ditukar karyawan menggunakan akumulasi koin emas yang mereka kumpulkan.",
              descEn: "Stock meaningful physical rewards, experiences, and digital vouchers for employees to redeem with their accumulated gold."
            }
          ]
        },
        player: {
          icon: "mdi-account-star",
          cardClass: "border-info",
          titleClass: "text-info",
          badgeClass: "badge-info text-dark",
          stepClass: "border-left-info",
          titleId: "Alur Perjalanan Game Player",
          titleEn: "Game Player Journey (Employee Experience)",
          subtitleId: "Fokus: Rutinitas Misi Harian, Konsistensi Streak, Kontribusi Boss Raid, dan Penukaran Hadiah.",
          subtitleEn: "Focus: Daily Habit Loops, Streak Consistency, Boss Raid Attacks, and Reward Redemptions.",
          steps: [
            {
              titleId: "Login Harian & Menjaga Daily Streak Multiplier",
              titleEn: "Daily Check-in & Maintaining Consecutive Streak Multipliers",
              descId: "Membuka platform setiap pagi untuk mengecek misi prioritas dan mempertahankan streak konsistensi agar multiplier bonus tetap aktif.",
              descEn: "Log in each workday morning to review priority quests and extend consecutive streaks for heightened multiplier bonuses."
            },
            {
              titleId: "Eksekusi Pekerjaan & Kirim Proof of Work (PoW)",
              titleEn: "Execute Tasks & Submit Verifiable Deliverables (PoW)",
              descId: "Menyelesaikan tugas kerja nyata dan melampirkan screenshot atau URL dokumen hasil pekerjaan ke formulir submission.",
              descEn: "Complete real-world operational deliverables and submit clear evidence links or photos for verification."
            },
            {
              titleId: "Menyerang Monster Boss Raid Bersama Divisi",
              titleEn: "Inflict Damage to Quarterly Team Boss Raids",
              descId: "Setiap penyelesaian quest otomatis mengonversi kontribusi kerja pemain menjadi serangan kerusakan (Damage) ke monster Boss Raid divisi.",
              descEn: "Each approved quest translates effort directly into attack damage against the shared quarterly Boss target."
            },
            {
              titleId: "Apresiasi Rekan Tim via Kudos Lounge",
              titleEn: "Peer-to-Peer Appreciation in Kudos Lounge",
              descId: "Mengirimkan Kudos (pujian dan bonus koin) kepada rekan kerja yang saling membantu menyelesaikan pekerjaan lintas departemen.",
              descEn: "Dispatch commendations and bonus recognition coins to colleagues who demonstrated exemplary support and teamwork."
            },
            {
              titleId: "Penukaran Hadiah di Loot Vault & Reward Shop",
              titleEn: "Reward Redemption in Reward Shop & Loot Vault",
              descId: "Menukarkan akumulasi Gold Coins dengan voucher makanan, saldo e-wallet, gadget, atau fasilitas ekstra kantor.",
              descEn: "Redeem hard-earned Gold Coins for food vouchers, e-wallet balances, office perks, or electronics."
            }
          ]
        },
        superadmin: {
          icon: "mdi-shield-check",
          cardClass: "border-danger",
          titleClass: "text-danger",
          badgeClass: "badge-danger text-white",
          stepClass: "border-left-danger",
          titleId: "Alur Kerja Utama Super Admin",
          titleEn: "Platform Operator Journey (Super Admin Console)",
          subtitleId: "Fokus: Tata Kelola Multi-Tenant, Approval Transaksi Finansial, Audit Log, dan Benchmarking Platform.",
          subtitleEn: "Focus: Multi-Tenant Governance, Billing Verifications, Security Audit Logs, and Platform Benchmarks.",
          steps: [
            {
              titleId: "Meninjau Inbound Leads & Demo Inquiries",
              titleEn: "Review Inbound Inquiries & Enterprise Leads",
              descId: "Memeriksa formulir pendaftaran perusahaan yang masuk dari landing page publik, mengonfirmasi kualifikasi, dan mengonversi menjadi workspace aktif.",
              descEn: "Evaluate enterprise demo registrations from the public landing page, qualify leads, and convert them to live workspaces."
            },
            {
              titleId: "Approval Invoice Transfer Bank Manual",
              titleEn: "Verify & Approve Manual Bank Invoices",
              descId: "Memverifikasi bukti transfer pembayaran invoice lisensi paket atau add-on kuota eceran sebelum kuota kursi ditambahkan otomatis ke perusahaan.",
              descEn: "Verify bank transfer slips for annual subscriptions or add-on seat expansions before automated credential provisioning."
            },
            {
              titleId: "Monitoring Audit Log & Keamanan Platform",
              titleEn: "Monitor Security Audit Logs & System Integrity",
              descId: "Menginspeksi log aktivitas seluruh tenant untuk mendeteksi anomali, serangan siber, atau manipulasi saldo gold.",
              descEn: "Inspect immutable audit trails across all tenant accounts to safeguard against unauthorized access or point irregularities."
            }
          ]
        }
      },

      terms: [
        {
          id: "gold",
          name: "Gold Coins",
          nameEn: "Gold Coins",
          english: "Virtual Corporate Currency",
          category: "currency",
          icon: "mdi-circle-multiple",
          impact: "positive",
          impactLabel: "Mata Uang Utama",
          impactLabelEn: "Primary Currency",
          definition: "Mata uang virtual primer di platform Gamiteam yang diperoleh karyawan setelah berhasil menyelesaikan quest dan tugas kerja. Gold ini memiliki nilai riil yang dapat ditukarkan di Reward Shop dengan barang, voucher, atau apresiasi nyata.",
          definitionEn: "The primary virtual currency on the Gamiteam platform earned by completing workplace quests. Gold coins hold tangible value and can be redeemed in the Reward Shop for real-world vouchers, gadgets, or perks.",
          workplaceExample: "Karyawan menyelesaikan KPI pembuatan modul software tepat waktu dan menerima 150 Gold, yang kemudian diakumulasikan untuk menukar voucher makan siang.",
          workplaceExampleEn: "A software engineer ships a sprint module on time, earning 150 Gold, which is saved toward an executive dining voucher.",
          formula: "Total_Gold = Σ(Gold_Misi) + Σ(Gold_Kudos) - Σ(Gold_Penalti) - Σ(Gold_Penukaran)",
          formulaEn: "Total_Gold = Σ(Gold_Quest) + Σ(Gold_Kudos) - Σ(Gold_Penalty) - Σ(Gold_Redeem)",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Pastikan nilai gold seimbang dengan tingkat kesulitan misi agar tidak memicu inflasi koin di perusahaan.",
          tipEn: "Calibrate gold payouts with task difficulty to prevent virtual currency inflation within the company."
        },
        {
          id: "exp",
          name: "EXP (Experience Points)",
          nameEn: "EXP (Experience Points)",
          english: "Character Progression",
          category: "currency",
          icon: "mdi-lightning-bolt",
          impact: "positive",
          impactLabel: "Progresi Karakter",
          impactLabelEn: "Character Progression",
          definition: "Poin pengalaman yang tidak pernah berkurang (non-depreciating) yang merefleksikan jam terbang, loyalitas, dan akumulasi kontribusi kerja karyawan sejak hari pertama bergabung.",
          definitionEn: "Non-depreciating progression points reflecting career tenure, skill accumulation, and lifetime contributions since day one.",
          workplaceExample: "Setiap laporan mingguan yang disubmit memberikan 50 EXP. Ketika mencapai 1000 EXP, karyawan naik pangkat dari 'Apprentice' menjadi 'Senior Specialist'.",
          workplaceExampleEn: "Submitting weekly reports awards 50 EXP. Hitting 1,000 EXP unlocks promotion from 'Apprentice' to 'Senior Specialist'.",
          formula: "Level = Floor(EXP / 100) + 1",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "EXP tidak bisa hangus meskipun koin emas Anda sudah ditukarkan menjadi hadiah di Reward Shop.",
          tipEn: "EXP never expires or depletes, even after redeeming all your Gold Coins in the Reward Shop."
        },
        {
          id: "streak",
          name: "Daily Streak",
          nameEn: "Daily Streak",
          english: "Consecutive Consistency Multiplier",
          category: "currency",
          icon: "mdi-fire",
          impact: "positive",
          impactLabel: "Pengali Bonus",
          impactLabelEn: "Multiplier Bonus",
          definition: "Indikator konsistensi harian saat pemain login dan menyelesaikan setidaknya 1 aktivitas kerja setiap hari secara berturut-turut tanpa jeda.",
          definitionEn: "A consecutive daily activity tracker rewarding continuous workplace engagement and habit formation without missed days.",
          workplaceExample: "Konsisten login dan update status harian selama 14 hari menghasilkan pengali Streak 1.25x untuk seluruh perolehan EXP dan Boss Damage.",
          workplaceExampleEn: "Checking in and completing tasks for 14 consecutive days awards a 1.25x multiplier to all incoming EXP and Boss Raid damage.",
          formula: "Multiplier = 1.0 + (Min(Streak, 30) × 0.05)",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "Jangan biarkan streak Anda terputus! 1 hari absen akan mereset hitungan streak kembali ke 0.",
          tipEn: "Maintain your momentum! Missing a single workday resets your active streak back to zero."
        },
        {
          id: "burn_rate",
          name: "Gold Burn Rate",
          nameEn: "Gold Burn Rate",
          english: "Currency Outflow Ratio",
          category: "currency",
          icon: "mdi-trending-down",
          impact: "system",
          impactLabel: "Metrik Finansial",
          impactLabelEn: "Financial Metric",
          definition: "Rasio perbandingan antara koin emas yang telah ditukarkan menjadi hadiah (outflow) dibandingkan dengan total koin emas yang diterbitkan dari quest (inflow).",
          definitionEn: "The ratio of redeemed gold (redemption outflow) to newly minted quest reward gold (minting inflow).",
          workplaceExample: "Jika tim HR menerbitkan 10.000 Gold dan karyawan menukar hadiah senilai 6.000 Gold, maka Burn Rate berada di angka ideal 60%.",
          workplaceExampleEn: "If 10,000 Gold is minted and employees redeem 6,000 Gold in rewards, the monthly burn rate sits at a healthy 60%.",
          formula: "Burn_Rate = (Gold_Ditukarkan / Total_Gold_Diterbitkan) × 100%",
          formulaEn: "Burn_Rate = (Gold_Redeemed / Total_Gold_Minted) × 100%",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Pantau angka ini di Executive Analytics. Jika di bawah 30%, buatlah katalog hadiah yang lebih menarik.",
          tipEn: "Monitor this metric in Executive Analytics. If below 30%, introduce more desirable items in the Reward Shop."
        },
        {
          id: "quest",
          name: "Quests (Misi KPI)",
          nameEn: "Quests (KPI Objectives)",
          english: "Performance Objectives",
          category: "quest",
          icon: "mdi-bullseye-arrow",
          impact: "positive",
          impactLabel: "Target Kerja",
          impactLabelEn: "Work Target",
          definition: "Tugas kerja terukur dengan kriteria penyelesaian jelas, batas waktu (deadline), dan imbalan berupa EXP dan Gold Coins yang dirancang oleh Game Maker.",
          definitionEn: "Actionable workplace objectives with measurable completion criteria, deadlines, and calibrated EXP/Gold payouts designed by Game Makers.",
          workplaceExample: "Misi Harian: 'Membersihkan meja kerja dan memilah 3 email prioritas' (20 Gold). Misi Kuartalan: 'Mencapai target sales 100 Juta' (1.500 Gold).",
          workplaceExampleEn: "Daily Quest: 'Resolve 3 priority tickets' (30 Gold). Quarterly Quest: 'Hit quarterly sales quota of $50k' (1,500 Gold).",
          formula: "Reward = Base_Gold × Difficulty_Tier (Easy: 1x, Medium: 2x, Hard: 5x)",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Gunakan deskripsi yang spesifik dan terukur agar tim tidak bingung saat mengirimkan bukti kerja.",
          tipEn: "Provide explicit deliverables and evidence guidelines so team members know exactly what is required."
        },
        {
          id: "habit_tracker",
          name: "Habit Tracker",
          nameEn: "Habit Tracker",
          english: "Micro-Behavior Loop",
          category: "quest",
          icon: "mdi-repeat",
          impact: "positive",
          impactLabel: "Rutinitas Mikro",
          impactLabelEn: "Micro Habit",
          definition: "Misi mikro yang berulang setiap hari atau setiap minggu untuk membentuk kebiasaan kerja positif (misalnya: hadir tepat waktu, standup meeting, 5S).",
          definitionEn: "Recurring daily or weekly micro-actions designed to reinforce positive operational habits (e.g. punctuality, daily standups).",
          workplaceExample: "Habit: 'Hadir sebelum pukul 08:30 WIB'. Pemain yang centang habit ini mendapatkan 10 Gold dan menjaga streak kedisiplinannya.",
          workplaceExampleEn: "Habit: 'Check in before 8:30 AM'. Ticking this daily habit awards 10 Gold and preserves streak consistency.",
          formula: "Monthly_Bonus = 30_Days_Completed ? +200_Bonus_Gold : 0",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Habit adalah cara terbaik menumbuhkan budaya kerja unggul secara organik tanpa paksaan.",
          tipEn: "Micro-habits are the most sustainable driver of organizational excellence without coercive management."
        },
        {
          id: "gemini_ocr",
          name: "Gemini AI Vision OCR",
          nameEn: "Gemini AI Vision OCR",
          english: "Automated Proof Verification",
          category: "quest",
          icon: "mdi-robot",
          impact: "system",
          impactLabel: "Teknologi AI",
          impactLabelEn: "AI Technology",
          definition: "Sistem kecerdasan buatan berbasis Google Gemini Vision yang memindai gambar tangkapan layar atau foto bukti submission untuk mendeteksi keaslian dan kesesuaian kriteria.",
          definitionEn: "Multimodal AI engine powered by Google Gemini Vision that inspects uploaded deliverables, receipts, and screenshots to verify task authenticity.",
          workplaceExample: "Karyawan mengunggah screenshot transaksi closing nasabah; Gemini AI membaca angka nominal, tanggal, dan nama sales secara otomatis dalam 2 detik.",
          workplaceExampleEn: "An employee uploads a sales agreement screenshot; Gemini OCR extracts invoice totals, dates, and sign-offs within 2 seconds.",
          formula: "Auto_Approval = AI_Confidence >= 0.85 && Text_Match == TRUE",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Aktifkan Gemini AI pada quest berulang agar manajer tidak kelelahan memverifikasi bukti kerja manual satu per satu.",
          tipEn: "Enable Gemini AI verification on high-volume quests to eliminate manual review bottlenecks for managers."
        },
        {
          id: "proof_of_work",
          name: "Proof of Work (PoW)",
          nameEn: "Proof of Work (PoW)",
          english: "Deliverable Evidence",
          category: "quest",
          icon: "mdi-camera-check",
          impact: "system",
          impactLabel: "Bukti Validasi",
          impactLabelEn: "Evidence Proof",
          definition: "Berkas lampiran berupa foto, berkas PDF, atau URL repositori/dokumen yang wajib disertakan oleh pemain saat mengklaim penyelesaian quest.",
          definitionEn: "Mandatory supporting documentation (screenshots, code PR links, receipts) submitted by players to validate quest completion.",
          workplaceExample: "Link pull request GitHub atau foto nota pengiriman barang yang diunggah ke formulir klaim quest.",
          workplaceExampleEn: "A link to a merged GitHub pull request or shipping manifest uploaded during quest submission.",
          formula: "N/A",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "Unggah gambar yang jelas dan beresolusi cukup agar proses approval berjalan instan tanpa revisi.",
          tipEn: "Upload high-resolution images and clear links to accelerate instant approval without revisions."
        },
        {
          id: "sop_consequence",
          name: "SOP Consequence (Pelanggaran)",
          nameEn: "SOP Consequence (Sanctions)",
          english: "Disciplinary Penalty",
          category: "discipline",
          icon: "mdi-alert-outline",
          impact: "negative",
          impactLabel: "Penalti Poin",
          impactLabelEn: "Point Penalty",
          definition: "Pengurangan Gold Coins otomatis atau manual akibat ketidakpatuhan terhadap SOP kerja yang telah disepakati bersama di perusahaan.",
          definitionEn: "Calibrated deduction of Gold Coins resulting from standard operating procedure (SOP) non-compliance.",
          workplaceExample: "Terlambat mengirimkan laporan mingguan lebih dari batas jam 17:00 dikenai penalti -30 Gold.",
          workplaceExampleEn: "Submitting a weekly client summary past the 5:00 PM cutoff incurs a calibrated -30 Gold consequence.",
          formula: "Gold_Akhir = Max(0, Gold_Saat_Ini - Penalty_Amount)",
          formulaEn: "Final_Gold = Max(0, Current_Gold - Penalty_Amount)",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Gunakan penalti sebagai instrumen edukasi, bukan untuk menghukum semata. Selalu sertakan alasan pelanggaran secara transparan.",
          tipEn: "Treat consequences as educational feedback rather than punishment. Always accompany deductions with constructive rationale."
        },
        {
          id: "direct_punishment",
          name: "Direct Punishment",
          nameEn: "Direct Punishment",
          english: "Immediate Incident Sanction",
          category: "discipline",
          icon: "mdi-octagon-outline",
          impact: "negative",
          impactLabel: "Sanksi Berat",
          impactLabelEn: "Severe Sanction",
          definition: "Tindakan disiplin berat yang diterapkan langsung oleh manajer atas insiden fatal (misalnya kelalaian keamanan kerja, kebocoran data, atau mangkir tanpa kabar).",
          definitionEn: "Immediate executive disciplinary sanction applied following critical incidents (security breaches, data leaks, unexplained absenteeism).",
          workplaceExample: "Meninggalkan server produksi mati tanpa koordinasi dikenai penalti langsung -200 Gold dan pembekuan klaim hadiah selama 7 hari.",
          workplaceExampleEn: "Causing uncoordinated production downtime results in an immediate -200 Gold penalty and 7-day reward store suspension.",
          formula: "N/A",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Sanksi berat harus terdokumentasi dan dapat diaudit di menu System Audit Logs.",
          tipEn: "Ensure severe incidents are fully documented and auditable in the System Audit Logs menu."
        },
        {
          id: "compensation_quest",
          name: "Misi Kompensasi (Pemulihan Penalti & Reputasi Poin)",
          nameEn: "Recovery Task (Compensation Quest)",
          english: "Reparation Task",
          category: "discipline",
          icon: "mdi-hand-heart",
          impact: "positive",
          impactLabel: "Pemulihan Poin",
          impactLabelEn: "Point Recovery",
          definition: "Mekanisme pemulihan yang adil di mana pemain yang terkena penalti dapat mengambil tugas darurat ekstra untuk menebus koin yang hilang dan mengembalikan reputasinya.",
          definitionEn: "A restorative justice mechanism enabling penalized employees to undertake extra service tasks to recoup lost gold and restore standings.",
          workplaceExample: "Karyawan yang terlambat mengambil misi kompensasi 'Merapikan inventaris kantor setelah jam kerja' untuk mendapatkan kembali 30 Gold yang hilang.",
          workplaceExampleEn: "An employee takes on an after-hours documentation audit task to recover 30 deducted Gold coins.",
          formula: "Net_Loss = Penalty_Gold - Compensation_Gold_Earned",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Gamiteam mengedepankan budaya perbaikan (growth mindset). Jangan biarkan pemain merasa putus asa karena poin negatif.",
          tipEn: "Gamiteam fosters a growth mindset. Never leave a motivated employee trapped in negative point territory."
        },
        {
          id: "world_realm",
          name: "World Realm (Dunia Virtual 3D)",
          nameEn: "World Realm (3D Virtual Space)",
          english: "Immersive 3D Corporate Space",
          category: "raid",
          icon: "mdi-earth",
          impact: "system",
          impactLabel: "Arena Visual",
          impactLabelEn: "Visual Arena",
          definition: "Ruang virtual 3D tempat seluruh anggota divisi perusahaan berinteraksi, memvisualisasikan progres kerja bersama, dan mengalahkan tantangan korporat.",
          definitionEn: "Interactive 3D virtual environment where cross-functional division members collaborate, visualize team momentum, and defeat corporate raids.",
          workplaceExample: "Tema Cyberpunk Metropolis di mana gedung-gedung bertambah terang saat target departemen tercapai.",
          workplaceExampleEn: "A Cyberpunk Citadel realm where city spires illuminate as team milestones are reached.",
          formula: "N/A",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Pilih tema Realm yang selaras dengan nilai dan budaya industri perusahaan Anda di Realm Marketplace.",
          tipEn: "Choose a theme in the Realm Marketplace that resonates with your company culture and creative identity."
        },
        {
          id: "boss_raid",
          name: "Boss Raid Kuartalan",
          nameEn: "Quarterly Boss Raid",
          english: "Cooperative Quarterly Target",
          category: "raid",
          icon: "mdi-sword-cross",
          impact: "positive",
          impactLabel: "Target Kolektif",
          impactLabelEn: "Collective Target",
          definition: "Monster bos raksasa yang melambangkan target besar perusahaan (misal: Boss Target Omzet Q4). Bos hanya bisa dikalahkan jika seluruh karyawan bahu-membahu menyumbangkan damage.",
          definitionEn: "A collective corporate challenge personified as a raid boss that can only be defeated through distributed cross-team quest completions.",
          workplaceExample: "Boss 'Titan Deadline' memiliki 50.000 HP. Setiap quest yang diselesaikan pemain mengurangi HP Boss secara proporsional.",
          workplaceExampleEn: "Boss 'Titan of Q4 Revenue' has 500,000 HP. Every verified quest completed chips away at the boss HP pool.",
          formula: "Boss_HP_Remaining = Boss_HP_Max - Σ(All_Players_Damage)",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Atur event Boss Raid setiap awal kuartal untuk memicu semangat kerja sama antar divisi (cross-functional alignment).",
          tipEn: "Launch Boss Raids at the beginning of each quarter to galvanize cross-departmental alignment."
        },
        {
          id: "damage_dealt",
          name: "Damage Dealt",
          nameEn: "Damage Dealt",
          english: "Raid Attack Contribution",
          category: "raid",
          icon: "mdi-sword-cross",
          impact: "positive",
          impactLabel: "Kontribusi Serangan",
          impactLabelEn: "Attack Contribution",
          definition: "Besaran pukulan kerusakan yang diberikan karakter pemain ke Boss Raid, dihitung dari kombinasi perolehan EXP, Gold, dan pengali Streak aktif pemain.",
          definitionEn: "Quantified raid damage inflicted by a player character on the boss, derived from completed EXP, Gold, and active streak multipliers.",
          workplaceExample: "Menyelesaikan quest bernilai 100 EXP dengan Streak 10 hari menghasilkan 250 Damage ke Monster Kuartalan.",
          workplaceExampleEn: "Completing a 100 EXP quest with a 10-day streak deals 250 damage to the quarterly raid boss.",
          formula: "Damage = (EXP + (Gold × 2)) × Multiplier_Streak",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "Semakin tinggi streak dan semakin sulit quest Anda, semakin besar damage yang Anda berikan ke Boss!",
          tipEn: "Higher streaks and difficult quests generate significantly amplified damage against the boss."
        },
        {
          id: "realm_loot_chest",
          name: "Realm Loot Chest",
          nameEn: "Realm Loot Chest",
          english: "Shared Raid Bounty",
          category: "raid",
          icon: "mdi-gift-outline",
          impact: "positive",
          impactLabel: "Hadiah Kolektif",
          impactLabelEn: "Shared Bounty",
          definition: "Peti harta karun bersama yang terbuka ketika Boss Raid berhasil dikalahkan, membagikan bonus pool koin emas ke seluruh pejuang yang berpartisipasi.",
          definitionEn: "A shared reward pool that unlocks upon boss defeat, distributing proportional gold bounties to contributing team members.",
          workplaceExample: "Ketika Titan Target Q4 tumbang, Loot Chest terbuka dan membagikan bonus 500 Gold ke masing-masing karyawan yang menyumbang minimal 1.000 Damage.",
          workplaceExampleEn: "When the Q4 Titan falls, the Loot Chest distributes 500 bonus Gold to every contributor who dealt at least 1,000 damage.",
          formula: "Individual_Share = Total_Bounty × (Player_Damage / Total_Damage)",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Hadiah bersama menciptakan rasa kepemilikan (collective ownership) terhadap kesuksesan perusahaan.",
          tipEn: "Shared bounties cultivate collective ownership and celebration around company milestones."
        },
        {
          id: "squad",
          name: "Squad / Divisi",
          nameEn: "Squad / Division",
          english: "Work Units & Guilds",
          category: "org",
          icon: "mdi-shield-check",
          impact: "system",
          impactLabel: "Struktur Organisasi",
          impactLabelEn: "Organizational Unit",
          definition: "Grup atau unit kerja internal (seperti Divisi Engineering, Sales, Marketing, HR) tempat pemain berkumpul dan bersaing di papan peringkat divisi.",
          definitionEn: "Functional organizational teams or guilds (e.g. Engineering, Sales, Customer Success) collaborating on squad leaderboards.",
          workplaceExample: "Squad 'Frontend Titans' vs 'Sales Warriors' berlomba memperebutkan gelar Divisi Paling Produktif bulan ini.",
          workplaceExampleEn: "Squad 'Engineering Titans' competes with 'Sales Guardians' for the Most Productive Division cup.",
          formula: "Squad_Score = Σ(Player_EXP) / Jumlah_Anggota_Squad",
          formulaEn: "Squad_Score = Σ(Player_EXP) / Total_Squad_Members",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Bagi skuad secara merata agar kompetisi antar divisi tetap sehat dan berimbang.",
          tipEn: "Balance squad sizes or use normalized averages to maintain fair and motivating division competitions."
        },
        {
          id: "leaderboard",
          name: "Leaderboard (Papan Peringkat)",
          nameEn: "Leaderboard (Rankings)",
          english: "Real-time Rankings",
          category: "org",
          icon: "mdi-trophy",
          impact: "positive",
          impactLabel: "Kompetisi Sehat",
          impactLabelEn: "Healthy Competition",
          definition: "Papan peringkat real-time yang memperlihatkan peringkat karyawan dan divisi berdasarkan perolehan EXP, Gold, dan damage raid.",
          definitionEn: "Real-time performance boards ranking individuals and divisions based on cumulative EXP, Gold, and Raid Damage contributions.",
          workplaceExample: "Peringkat 1 hingga 3 bulanan di leaderboard individu mendapatkan plakat penghargaan dan ekstra voucher makan malam.",
          workplaceExampleEn: "Top 3 individuals on the monthly leaderboard receive honorary badges and dining vouchers.",
          formula: "Rank_Sorting = ORDER BY total_xp DESC, total_gold DESC",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "Lihat posisi Anda setiap pekan dan tantang diri Anda untuk masuk ke posisi Top 5 Leaderboard!",
          tipEn: "Check your standings weekly and challenge yourself to reach the Top 5 tier."
        },
        {
          id: "kudos_lounge",
          name: "Kudos Lounge",
          nameEn: "Kudos Lounge",
          english: "Peer-to-Peer Recognition",
          category: "org",
          icon: "mdi-message-star",
          impact: "positive",
          impactLabel: "Apresiasi Rekan",
          impactLabelEn: "Peer Recognition",
          definition: "Ruang apresiasi sosial antar rekan kerja di mana setiap pemain dapat mengirimkan ucapan terima kasih dan sedikit koin emas kepada sesama kolega.",
          definitionEn: "A social recognition hub allowing peers to send heartfelt appreciation notes and micro-gold allocations to colleagues.",
          workplaceExample: "'Terima kasih Ahmad sudah membantu deploy server jam 8 malam!' disertai pemberian 10 Gold dari kuota bulanan.",
          workplaceExampleEn: "'Thank you Sarah for resolving the client outage at 8 PM!' accompanied by 10 Gold from the sender's allowance.",
          formula: "Monthly_Kudos_Quota = 50 Gold / Karyawan (Reset setiap awal bulan)",
          formulaEn: "Monthly_Kudos_Quota = 50 Gold / Employee (Resets monthly)",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "Kirimkan apresiasi kepada rekan kerja yang berjasa; apresiasi kecil berdampak besar bagi motivasi tim!",
          tipEn: "Take a moment to recognize helpful teammates; micro-appreciation builds lasting morale."
        },
        {
          id: "seat_quota",
          name: "Seat Quota (Kapasitas Kursi)",
          nameEn: "Seat Quota (License Capacity)",
          english: "Active License Seats",
          category: "billing",
          icon: "mdi-seat-outline",
          impact: "system",
          impactLabel: "Kapasitas Lisensi",
          impactLabelEn: "License Capacity",
          definition: "Jumlah maksimum akun karyawan yang dapat aktif bermain dan menyelesaikan quest di dalam workspace perusahaan secara bersamaan.",
          definitionEn: "The maximum number of active player accounts permitted to log in, complete quests, and earn rewards concurrently in an enterprise workspace.",
          workplaceExample: "Perusahaan membeli lisensi Growth dengan 50 Seat Quota; 48 kursi telah terpakai oleh karyawan aktif.",
          workplaceExampleEn: "A company subscribes to a Growth plan with 50 player seats; 48 seats are currently assigned to active team members.",
          formula: "Sisa_Seat = Total_Purchased_Seats - Total_Active_Users",
          formulaEn: "Remaining_Seats = Total_Purchased_Seats - Total_Active_Users",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Jika kuota kursi hampir penuh, segera pesan kuota eceran melalui menu Billing & Quota agar rekrutan baru langsung bisa bermain.",
          tipEn: "When approaching maximum capacity, purchase add-on seats in the Billing & Quota menu so new hires can onboard seamlessly."
        },
        {
          id: "addon_quota",
          name: "Add-on Kuota Eceran",
          nameEn: "Add-on Seat Quota",
          english: "On-demand License Expansion",
          category: "billing",
          icon: "mdi-plus-box",
          impact: "system",
          impactLabel: "Ekspansi Instan",
          impactLabelEn: "Instant Expansion",
          definition: "Fitur pembelian kuota kursi karyawan tambahan secara eceran per orang tanpa harus melakukan upgrade paket tahunan berskala besar.",
          definitionEn: "An on-demand capability to expand workspace seat limits per player without altering existing base subscription tiers.",
          workplaceExample: "Menambah 5 karyawan magang di tengah bulan cukup dengan membeli 5 seat eceran seharga Rp 15.000/seat.",
          workplaceExampleEn: "Onboarding 5 seasonal interns by purchasing 5 add-on seats at IDR 15,000 per seat per month.",
          formula: "Biaya_Addon = Jumlah_Seat × Harga_Satuan × Durasi_Bulan",
          formulaEn: "Addon_Cost = Seat_Count × Unit_Price × Duration_Months",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Sangat fleksibel untuk perusahaan yang memiliki karyawan musiman, pekerja lepas, atau peserta magang.",
          tipEn: "Ideal for agile organizations managing seasonal hires, contractors, or project interns."
        },
        {
          id: "midtrans",
          name: "Midtrans Payment Gateway",
          nameEn: "Midtrans Payment Gateway",
          english: "Automated Instant Checkout",
          category: "billing",
          icon: "mdi-credit-card-check",
          impact: "system",
          impactLabel: "Otomasi Transaksi",
          impactLabelEn: "Automated Checkout",
          definition: "Kanal pembayaran otomatis (QRIS, BCA Virtual Account, Mandiri, BNI, Kartu Kredit) yang langsung memverifikasi transaksi dan mengaktifkan kuota kursi seketika.",
          definitionEn: "Automated payment processing (QRIS, Virtual Accounts, Credit Cards) with real-time webhooks for instantaneous license activation.",
          workplaceExample: "Owner scan QRIS lewat aplikasi mobile banking; dalam 3 detik status invoice berubah menjadi 'Paid' dan kuota bertambah.",
          workplaceExampleEn: "An owner scans a dynamic QRIS code via mobile banking; invoice status flips to 'Paid' and seats unlock in 3 seconds.",
          formula: "N/A",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Gunakan pembayaran otomatis Midtrans agar tidak perlu menunggu verifikasi transfer manual oleh admin.",
          tipEn: "Use instant payment methods to bypass manual bank transfer verification queues."
        },
        {
          id: "game_owner",
          name: "Game Owner",
          nameEn: "Game Owner",
          english: "Enterprise Executive & Sponsor",
          category: "role",
          icon: "mdi-crown",
          impact: "system",
          impactLabel: "Peran Utama",
          impactLabelEn: "Executive Role",
          definition: "Pemilik akun perusahaan (Direktur, CEO, atau Head of People) yang memiliki wewenang penuh atas pembayaran paket, alokasi anggaran hadiah, dan akses analitik makro.",
          definitionEn: "The corporate executive account (CEO, Founder, Head of People) holding complete authority over licensing, budget allocations, and macro analytics.",
          workplaceExample: "Alexander Pratama mengelola workspace PT Solusi Jaya, menentukan anggaran gold bulanan sebesar Rp 10 Juta, dan memantau ROI tim.",
          workplaceExampleEn: "The CEO sets the monthly reward pool liability, configures macro gamification rules, and evaluates ROI in Executive Analytics.",
          formula: "N/A",
          targetRole: "Game Owner",
          targetRoleEn: "Game Owner",
          tip: "Jadikan Gamiteam sebagai dashboard budaya perusahaan untuk melihat performa tanpa perlu micromanagement.",
          tipEn: "Leverage Gamiteam as your cultural cockpit to monitor organizational velocity without micromanagement."
        },
        {
          id: "game_maker",
          name: "Game Maker",
          nameEn: "Game Maker",
          english: "Gamification Architect & Operations",
          category: "role",
          icon: "mdi-hammer-wrench",
          impact: "system",
          impactLabel: "Peran Operasional",
          impactLabelEn: "Operational Architect",
          definition: "Arsitek gamifikasi (HR Manager, Team Lead, atau Product Manager) yang bertugas merancang quest, aturan tata tertib SOP, dan memverifikasi hasil kerja pemain.",
          definitionEn: "Operational architects (HR Leaders, Division Heads) tasked with crafting quests, managing SOP consequences, and reviewing deliverables.",
          workplaceExample: "Rendra Wibowo membuat 12 quest sprint untuk tim Engineering dan mengecek bukti submit setiap sore.",
          workplaceExampleEn: "A team lead crafts 12 sprint quests, configures OCR guidelines, and conducts end-of-day proof verification.",
          formula: "N/A",
          targetRole: "Game Maker",
          targetRoleEn: "Game Maker",
          tip: "Kombinasikan quest jangka pendek harian dengan quest jangka panjang kuartalan agar motivasi tim tidak pernah padam.",
          tipEn: "Blend short-term daily habit loops with quarterly milestone quests to sustain team drive year-round."
        },
        {
          id: "game_player",
          name: "Game Player",
          nameEn: "Game Player",
          english: "Team Member & Quest Adventurer",
          category: "role",
          icon: "mdi-account-star",
          impact: "system",
          impactLabel: "Peran Karyawan",
          impactLabelEn: "Employee Adventurer",
          definition: "Karyawan atau anggota tim yang login setiap hari untuk melihat misi kerja, mengirimkan bukti pekerjaan, melawan monster bos, dan menukarkan koin emas.",
          definitionEn: "Team members who log in to execute daily objectives, upload verifiable deliverables, battle quarterly raid bosses, and redeem rewards.",
          workplaceExample: "Dimas Anggoro mengumpulkan 400 Gold dari menyelesaikan tiket bug fixing dan menukarnya dengan voucher kopi.",
          workplaceExampleEn: "A designer earns 400 Gold across two sprints, trading it in for workplace coffee vouchers.",
          formula: "N/A",
          targetRole: "Game Player",
          targetRoleEn: "Game Player",
          tip: "Selesaikan quest harian di awal jam kerja untuk mengamankan poin dan streak harian Anda!",
          tipEn: "Complete key habit quests early in your workday to secure bonus streaks and team leaderboard points."
        },
        {
          id: "superadmin",
          name: "Super Admin",
          nameEn: "Super Admin",
          english: "Platform Operator & Master Admin",
          category: "role",
          icon: "mdi-shield-check",
          impact: "system",
          impactLabel: "Peran Platform",
          impactLabelEn: "Platform Operator",
          definition: "Pengelola pusat seluruh platform SaaS Gamiteam yang mengontrol lisensi global, onboarding perusahaan mitra, verifikasi transfer bank, dan audit integritas sistem.",
          definitionEn: "The master platform operator overseeing global multi-tenant workspaces, bank transfer approvals, audit logging, and infrastructure health.",
          workplaceExample: "Mengonfirmasi invoice pembayaran manual dari mitra baru dan memastikan kesehatan database serta server cloud.",
          workplaceExampleEn: "Verifying manual invoice wire transfers for new enterprise clients and maintaining platform integrity.",
          formula: "N/A",
          targetRole: "Super Admin",
          targetRoleEn: "Super Admin",
          tip: "Selalu tinjau Inbound Leads secara berkala untuk menjaga rasio konversi mitra baru tetap tinggi.",
          tipEn: "Regularly audit inbound leads to maintain healthy enterprise pipeline conversion rates."
        }
      ],

      faqs: [
        {
          qId: "Apakah koin emas (Gold) bisa diubah menjadi uang tunai langsung?",
          qEn: "Can virtual Gold Coins be converted directly to cash?",
          aId: "Tergantung kebijakan Game Owner di masing-masing perusahaan. Sebagian besar perusahaan menyediakan katalog hadiah berupa voucher e-wallet (GoPay, OVO, ShopeePay), voucher belanja, merchandise kantor, atau fasilitas tambahan seperti cuti setengah hari.",
          aEn: "This depends on company policy configured by your Game Owner. Most enterprises offer digital rewards including e-wallet top-ups (GoPay, OVO), retail vouchers, branded merchandise, or office perks such as half-day leaves."
        },
        {
          qId: "Apa yang terjadi jika karyawan terkena penalti poin (SOP Consequence)?",
          qEn: "What happens when an employee receives an SOP point deduction?",
          aId: "Gold koin karyawan akan berkurang sesuai nominal sanksi yang ditetapkan. Namun, karyawan tidak perlu khawatir karena mereka dapat mengambil Misi Kompensasi (Pemulihan Penalti & Reputasi Poin) untuk mengembalikan poin yang hilang dengan menyelesaikan tugas darurat ekstra.",
          aEn: "The employee's gold balance decreases by the stipulated sanction. However, players can undertake restorative Recovery Tasks (Compensation Quests) to recoup deducted points through extra contributions."
        },
        {
          qId: "Bagaimana cara kerja Gemini AI Vision OCR dalam verifikasi bukti kerja?",
          qEn: "How does Gemini AI Vision OCR verify proof of work submissions?",
          aId: "Saat karyawan mengunggah foto atau screenshot hasil kerja, model AI Gemini Vision akan memindai teks pada gambar, memvalidasi apakah tanggal, nominal, dan isi dokumen sesuai dengan target quest. Jika cocok, sistem bisa langsung menyetujui klaim tanpa menunggu approval manual dari manajer.",
          aEn: "Upon deliverable upload, the Gemini Vision AI model extracts textual telemetry (dates, invoice numbers, sign-offs) against the quest rubric. If confidence criteria match, the system approves the submission automatically without manual review."
        },
        {
          qId: "Kapan Boss Raid Kuartalan di-reset?",
          qEn: "When does the Quarterly Boss Raid season reset?",
          aId: "Boss Raid biasanya berlangsung selama 1 kuartal (3 bulan) atau sesuai jadwal yang diatur oleh Game Owner. Ketika Boss berhasil dikalahkan, seluruh pemain yang berpartisipasi mendapatkan porsi dari Loot Chest, dan monster bos baru dengan tema baru akan muncul pada musim berikutnya.",
          aEn: "Boss Raids run on quarterly 90-day cycles configured by the Game Owner. Defeating the boss opens the shared Loot Chest for all contributors, followed by a new themed realm challenge for the upcoming quarter."
        },
        {
          qId: "Bagaimana jika perusahaan ingin menambah karyawan di tengah masa langganan?",
          qEn: "How do enterprises add seats mid-subscription?",
          aId: "Game Owner dapat menggunakan fitur 'Add-on Kuota Eceran' di menu Billing & Quota. Pembelian dapat dilakukan per kursi (seat) dan langsung aktif seketika setelah pembayaran via QRIS/Virtual Account Midtrans.",
          aEn: "Game Owners can use the 'Add-on Seat Quota' feature in the Billing & Quota section. Additional seats can be provisioned instantly per user with automated payment gateways."
        }
      ]
    };
  },
  computed: {
    isEn() {
      return this.$currentLocale === "en";
    },

    displayedCategories() {
      return this.categories.map(c => ({
        id: c.id,
        label: this.isEn ? c.labelEn : c.labelId
      }));
    },

    currentWorkflowData() {
      return this.roleWorkflows[this.selectedRoleWorkflow] || this.roleWorkflows.owner;
    },

    filteredTerms() {
      return this.terms.filter(term => {
        const matchesCategory =
          this.selectedCategory === "all" || term.category === this.selectedCategory;

        if (!matchesCategory) return false;

        if (!this.searchQuery) return true;

        const q = this.searchQuery.toLowerCase().trim();
        const nameToSearch = this.isEn ? (term.nameEn || term.name) : term.name;
        const defToSearch = this.isEn ? (term.definitionEn || term.definition) : term.definition;
        const exToSearch = this.isEn ? (term.workplaceExampleEn || term.workplaceExample) : term.workplaceExample;
        const tipToSearch = this.isEn ? (term.tipEn || term.tip) : term.tip;

        return (
          nameToSearch.toLowerCase().includes(q) ||
          term.name.toLowerCase().includes(q) ||
          (term.english && term.english.toLowerCase().includes(q)) ||
          defToSearch.toLowerCase().includes(q) ||
          exToSearch.toLowerCase().includes(q) ||
          (term.formula && term.formula.toLowerCase().includes(q)) ||
          tipToSearch.toLowerCase().includes(q)
        );
      });
    },

    calculatedLevel() {
      return Math.floor((this.calcExp || 0) / 100) + 1;
    },

    currentTierName() {
      const lv = this.calculatedLevel;
      if (lv >= 50) return "Mythic Sovereign";
      if (lv >= 30) return "Grand Master";
      if (lv >= 20) return "Master Architect";
      if (lv >= 10) return "Expert Adventurer";
      if (lv >= 5) return "Rising Champion";
      return "Novice Explorer";
    },

    calculatedStreakMultiplier() {
      const s = Math.min(Math.max(this.calcStreak || 1, 1), 30);
      return (1.0 + s * 0.05).toFixed(2);
    },

    calculatedBossDamage() {
      const exp = Number(this.calcExp) || 0;
      const gold = Number(this.calcGoldEarned) || 0;
      const mult = Number(this.calculatedStreakMultiplier);
      return Math.round((exp + gold * 2) * mult);
    },

    calculatedBurnRate() {
      const earned = Number(this.calcGoldEarned) || 1;
      const redeemed = Number(this.calcGoldRedeemed) || 0;
      return Math.min(100, Math.round((redeemed / earned) * 100));
    },

    burnRateStatus() {
      const rate = this.calculatedBurnRate;
      if (rate < 30) return this.isEn ? "Hoarded / Low Activity" : "Poin Tertimbun / Rendah";
      if (rate <= 75) return this.isEn ? "Healthy & Stable Liquidity" : "Likuiditas Sehat & Stabil";
      return this.isEn ? "High / Liability Risk" : "Tinggi / Perlu Kontrol Liabilitas";
    },

    burnRateColor() {
      const rate = this.calculatedBurnRate;
      if (rate < 30) return "text-info";
      if (rate <= 75) return "text-success font-weight-bold";
      return "text-warning font-weight-bold";
    }
  },
  methods: {
    getCategoryLabel(catId) {
      const found = this.categories.find(c => c.id === catId);
      if (!found) return catId;
      return this.isEn ? found.labelEn : found.labelId;
    },

    getCategoryBadgeClass(catId) {
      switch (catId) {
        case "currency":
          return "badge-outline-warning text-warning";
        case "quest":
          return "badge-outline-success text-success";
        case "discipline":
          return "badge-outline-danger text-danger";
        case "raid":
          return "badge-outline-primary text-primary";
        case "org":
          return "badge-outline-info text-info";
        case "role":
          return "badge-outline-light text-light";
        case "billing":
          return "badge-outline-warning text-white";
        default:
          return "badge-outline-secondary";
      }
    },

    getImpactBadgeClass(impact) {
      switch (impact) {
        case "positive":
          return "badge-success text-dark font-weight-bold";
        case "negative":
          return "badge-danger text-white font-weight-bold";
        case "system":
          return "badge-secondary text-light";
        default:
          return "badge-info text-dark";
      }
    },

    getImpactIcon(impact) {
      switch (impact) {
        case "positive":
          return "mdi mdi-arrow-up-bold-circle";
        case "negative":
          return "mdi mdi-arrow-down-bold-circle";
        case "system":
          return "mdi mdi-cog-outline";
        default:
          return "mdi mdi-information-outline";
      }
    },

    toggleFaq(index) {
      this.expandedFaq = this.expandedFaq === index ? null : index;
    },

    printOrExport() {
      window.print();
    }
  }
};
</script>

<style scoped>
.glossary-view {
  font-family: inherit;
}

.custom-pills .nav-link {
  color: #94a3b8;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
}

.custom-pills .nav-link.active {
  background-color: #3b82f6;
  color: #ffffff;
}

.custom-pills .nav-link:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #f8fafc;
}

.term-card {
  border-radius: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.term-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
  border-color: #3b82f6 !important;
}

.border-left-warning {
  border-left: 4px solid #ff9900 !important;
}

.border-left-success {
  border-left: 4px solid #10b981 !important;
}

.border-left-info {
  border-left: 4px solid #06b6d4 !important;
}

.border-left-danger {
  border-left: 4px solid #ef4444 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.font-monospace {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
}

@media print {
  .nav-pills,
  .btn,
  .input-group,
  .sidebar,
  .navbar {
    display: none !important;
  }
  .card {
    background: #ffffff !important;
    color: #000000 !important;
    border: 1px solid #cccccc !important;
  }
  .text-white, .text-light, .text-muted {
    color: #111827 !important;
  }
}
</style>
